import { IUser } from '../types/user'

/**
 * Which ForeSight terms revision a user agreed to, and whether to ask again.
 *
 * There is no stored version. Acceptance is a single timestamp,
 * users.terms_accepted_at, so the revision someone agreed to is derived from
 * when they agreed: the latest revision effective at or before that moment.
 * A stored version column would need a migration and buy nothing, because
 * acceptTerms overwrites the timestamp on every acceptance — so no shape
 * preserves a prior acceptance. Proving "this user accepted this document on
 * this date" needs its own table, and that table can be backfilled from these
 * timestamps through versionAt whenever it is wanted.
 *
 * This lives in the client because the gate already does (Auth.tsx). The
 * server owns the acceptance record and nothing else here: a server-computed
 * boolean the client is free to ignore would be exactly as bypassable, and
 * would have to be hand-placed into two unrelated versions of UserType, since
 * master predates the GraphQL schema cutover.
 *
 * The terms TEXT is not here either. TermsAndConditions.tsx embeds the live
 * Termly documents, so revised wording reaches users with no deploy at all —
 * the 2026-11-03 AI revision went live that way. What this file controls is
 * only the re-prompt: whether an existing acceptance still counts.
 */

export interface ITermsRevision {
  /** Stable label for the revision; the date its text went live. */
  version: string
  /** When the text went live in Termly, ISO 8601 UTC — not when it deployed. */
  effectiveAt: string
  eulaUuid: string
  privacyUuid: string
}

/**
 * Each revision, oldest first; the last entry is current.
 *
 * The record starts at the AI revision. Earlier acceptances are real but
 * predate any version record, so versionAt returns null for them rather than
 * naming a revision we cannot evidence. Append when terms are revised; never
 * edit an existing entry, or acceptances already derived from it silently
 * change meaning.
 */
export const TERMS_REVISIONS: ITermsRevision[] = [
  {
    version: '2026-11-03',
    effectiveAt: '2026-11-03T17:00:00Z',
    eulaUuid: '8bce605e-1d24-4202-88b9-07498adc4640',
    privacyUuid: '617f875f-8a23-4a56-9b0b-c544684d2f5a',
  },
]

/**
 * Who is asked again when the terms change.
 *
 * Managers only: the assistant is a manager-facing feature, so technicians
 * have nothing to acknowledge and would be prompted at 5am as they start
 * their routes, over something they cannot reach. Planners and internal users
 * cannot reach it either.
 *
 * Role, not client, is what scopes this: both clients authenticate through
 * the same `me` query, so there is nothing to distinguish a web session from
 * a mobile one.
 *
 * 'manager' is the server's enum value. The composite labels in UserRole —
 * 'checkout_manager', 'general_and_checkout_manager' — are built in the
 * user-management screens for displaying OTHER users and never arrive on the
 * authenticated user, which is why the rest of the app also tests
 * `user.role === 'manager'`.
 */
export const REPROMPT_ROLES: string[] = ['manager']

export const currentRevision = (): ITermsRevision =>
  TERMS_REVISIONS[TERMS_REVISIONS.length - 1]

/**
 * The revision an acceptance corresponds to, or null when it predates the
 * version record or cannot be read. Pure over existing data, so it can
 * backfill an acceptance table later without any of this being stored.
 */
export const versionAt = (acceptedAt?: string | null): string | null => {
  const accepted = parse(acceptedAt)
  if (accepted === null) return null

  for (let i = TERMS_REVISIONS.length - 1; i >= 0; i -= 1) {
    const revision = TERMS_REVISIONS[i]
    if (accepted >= Date.parse(revision.effectiveAt)) return revision.version
  }
  return null
}

/**
 * Never accepted anything: asked, exactly as the previous gate did, and for
 * every role. Accepted an earlier revision: asked only if the role is in
 * scope, so nothing changes for technicians.
 */
export const acceptanceRequired = (user?: IUser | null): boolean => {
  if (!user) return false

  const accepted = parse(user.termsAcceptedAt)
  // An unreadable timestamp is treated as not yet accepted. Prompting someone
  // who did not need it costs a click; skipping someone who did leaves them
  // bound to terms they were never shown.
  if (accepted === null) return true

  if (!REPROMPT_ROLES.includes(user.role)) return false

  return accepted < Date.parse(currentRevision().effectiveAt)
}

const parse = (value?: string | null): number | null => {
  if (!value) return null

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}
